import Button from '@mui/material/Button';
import { GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { EnhancedDialog } from 'components/enhanced/dialog/EnhancedDialog';
import { EnhancedTabs, TabItem } from 'components/enhanced/tabs/EnhancedTabs';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import TenantPage from 'layouts/TenantPage';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { contractsApi } from '../api/contracts.api';
import ContractInsights from '../components/ContractInsights';
import ResetResidualUnitsPreviewDialog from '../components/dialogs/ResetResidualUnitsPreviwDialog';
import UpdateContractEquipmentDialog from '../components/dialogs/UpdateContractEquipmentDialog';
import { UpdateContractLineDialog } from '../components/dialogs/UpdateContractLineDialog';
import AdditionalEquipmentCostForm from '../components/form/AdditionalEquipmentCostForm';
import ContractEquipmentForm from '../components/form/ContractEquipmentForm';
import ContractHeaderForm from '../components/form/ContractHeaderForm';
import ContractLineForm from '../components/form/ContractLineForm';
import { useContractMutations } from '../hooks/mutations/useContractMutations';
import { useContract } from '../hooks/queries/useContract';
import { useContractEditBootstrap } from '../hooks/queries/useContractEditBootstrap';
import { useResetResidualUnitsPreview } from '../hooks/queries/useResetResidualUnitsPreview';
import {
  toAdditionalCostPayload,
  toContractEquipmentPayload,
  toContractLinePayload
} from '../mappers/contracts.mappers';
import { ContractLine } from '../types';
import ProductWarehouseForm from 'features/inventory/warehouses/components/form/ProductWarehouseForm';
import { contractService } from 'shared/services';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { ContractEquipmentColumns } from '../contract-equipments';
import { ContractLinesColumns } from '../contract-lines';
import { AdditionalEquipmentCostColumns } from '../additional-equipment-costs';

const DOWNLOADABLE_FIELDS = ['commercial_bid', 'bid_approval'];

export const EditContract = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const translationPrefix = 'pages.contract.edit';

  const datagridRefresh = useRef(null);
  const contractLinesRefresh = useRef(null);
  const handleRefreshLines = useCallback(() => contractLinesRefresh.current?.(), []);
  const additionalCostsRefresh = useRef(null);

  const isSupplierRole = useIsSupplier();
  const isTenantAdmin = useIsBottomFromTenantAdmin();

  const { contractId } = useParams();
  const parsedContractId = Number(contractId);
  const { contract } = useContract(parsedContractId);
  const {
    equipments,
    products,
    categories,
    warehouses,
    suppliers,
    laboratories,
    statusOptions,
    labelOptions
  } = useContractEditBootstrap();
  const {
    assignContractLine,
    addContractEquipment,
    updateContractEquipment,
    deleteContractEquipment,
    addAdditionalCost
  } = useContractMutations();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState<boolean>(false);
  const [contracts, setContracts] = useState<Option[]>([]);

  const handleFormSubmit = async (values) => {
    try {
      dispatch(loadingActions.startLoading());
      const payload = {
        ...values,
        ticketing_email: values.ticketing_email ?? ''
      };
      contractService.update(Number(contractId), payload).then((res) => {
        if (res.id) {
          toast.crud.successUpdate('Contratto');
        }
      });
    } catch (error) {
      console.error('Network transaction failed:', error);
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  // ---------------------------------------------------------- CONTRACT-LINE-LOGIC
  const [editContractLineRow, setEditContractLineRow] = useState<any>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const {
    open: openResetPreviewDialog,
    rows: resetPreviewRows,
    selectedLineIds: selectedResetLineIds,
    isLoadingPreview: isLoadingResetPreview,
    isApplying: isApplyingReset,
    allSelected: allResetLinesSelected,
    openDialog: openResetDialog,
    closeDialog: closeResetDialog,
    toggleLine: toggleResetLineSelection,
    selectAll: selectAllResetLines,
    deselectAll: deselectAllResetLines,
    apply: applyResetResidualUnits
  } = useResetResidualUnitsPreview({
    contractId,
    onApplied: () => contractLinesRefresh.current?.()
  });

  const handleClose = () => {
    setThresholdDialogOpen(false);
    setSelectedProductId(null);
  };

  // ----------------------------------------------------------------------ADD NEW CONTRACT
  const onLineSubmit = async (values) => {
    const warehouseId = values.warehouse;
    const lineToAdd: ContractLine = toContractLinePayload(values);

    setSelectedProductId(values.product.id);

    try {
      const res = await contractsApi.getProductWarehouseByProductAndWarehouse(
        values.product.id,
        warehouseId
      );
      if (res?.count === 0) {
        setSelectedWarehouseId(warehouseId);
        setThresholdDialogOpen(true);
      }
    } catch (error) {
      console.error('Errore nel controllo del magazzino:', error);
    }

    await assignContractLine(parsedContractId, lineToAdd).then(() => {
      toast.success(t('actions.successfully.added'));
      contractLinesRefresh.current();
    });
  };

  // ---------------------------------------------------------------------- CONTRACT-LINE EDIT + DELETE
  // Function to open the edit dialog and set the current row data to be edited
  const setOpenDialog = (row: any) => {
    setEditContractLineRow(row); // Set the current row data to be edited
    setOpenEditDialog(true); // Open the edit dialog
  };

  // Function to close the edit dialog
  const setCloseDialog = () => {
    setOpenEditDialog(false); // Close the edit dialog
  };

  // Define the columns for the contract line datagrid, with actions for opening the dialog and deleting
  const contractLineColumns: GridColDef[] = ContractLinesColumns(
    contractLinesRefresh,
    statusOptions,
    setOpenDialog,
    isSupplierRole
  );

  // ---------------------------------------------------------------------- EQUIPMENT-LOGIC

  const [editEquipmentRow, setEditEquipmentRow] = useState<any>(null);
  const [openEditEquipmentDialog, setOpenEditEquipmentDialog] = useState(false);

  const setOpenEquipmentDialog = (row: any) => {
    setEditEquipmentRow(row);
    setOpenEditEquipmentDialog(true);
  };

  const setCloseEquipmentDialog = () => {
    setOpenEditEquipmentDialog(false);
    setEditEquipmentRow(null);
  };

  const onSubmitContractEquipment = (values) => {
    // Controlla e sostituisci solo se il valore esiste
    const purchase_amount = values?.purchase_amount
      ? values.purchase_amount.replace(',', '.')
      : null;
    const yearly_lease_amount = values?.yearly_lease_amount
      ? values.yearly_lease_amount.replace(',', '.')
      : null;

    // Controlla se almeno uno è presente
    if (!purchase_amount && !yearly_lease_amount) {
      toast.warning(
        t('pages.contract-equipment.edit.only_one_allowed', {
          field1: t('entities.contract-equipment.yearly_lease_amount'),
          field2: t('entities.contract-equipment.purchase_amount')
        })
      );
      return;
    }

    const payload = toContractEquipmentPayload(parsedContractId, values);

    addContractEquipment(payload).then(() => {
      datagridRefresh.current();
    });
  };

  const onEditContractEquipment = (id: number, params: any) => {
    updateContractEquipment(id, params).then(() => {
      datagridRefresh.current();
      setCloseEquipmentDialog();
    });
  };

  const addThreshold = async (values) => {
    try {
      // Soglia non esiste, procedi con l'aggiunta
      const payload = {
        ...values,
        warehouse: selectedWarehouseId,
        product: selectedProductId,
        label: labelOptions.find((l) => l.id === values.label)?.code ?? values.label
      };

      await productWarehouseService.createUnifiedInstance(payload);
      toast.success(t('pages.contract.edit.threshold-created'));
      handleClose();
      return;
    } catch (error) {
      console.error(`Errore durante il controllo del magazzino ${selectedWarehouseId}:`, error);
    }

    handleClose();
  };

  const onDeleteContractEquipment = (value) => {
    // Call the delete service and show a success toast message on completion
    deleteContractEquipment(value).then(() => {
      // Refresh the datagrid
      datagridRefresh.current();
    });
  };

  const equipmentColumns = ContractEquipmentColumns(
    setOpenEquipmentDialog,
    onDeleteContractEquipment,
    isSupplierRole
  );

  //---------------------------------------------------------------------- ADD-COST-LOGIC
  const additionalCostColumns = AdditionalEquipmentCostColumns();

  const onAdditionalCostSubmit = (values) => {
    const payload = toAdditionalCostPayload(parsedContractId, values);
    addAdditionalCost(payload).then(() => {
      additionalCostsRefresh.current();
    });
  };

  // 27/06/2025: Tenuta per ragiorni di sicurezza, ora la logica è dirversa
  // const onSubmitContractLineEdit = async (values) => {
  //   dispatch(loadingActions.startLoading());
  //   contractLineService
  //     .update(selectedItem.id, { ...values, product: values.product.id })
  //     .then(() => {
  //       enqueueSnackbar('Contract line updated successfully', { variant: 'success' });
  //       contractLineService
  //         .getAllPaginated(1000, 0, { contract_header: contractId })
  //         .then((res) => setContractLines(res.results));
  //     });
  //   dispatch(loadingActions.stopLoading());
  //   setSelectedItem(null);
  //   setOpenDialog(false);
  // };

  const memoizedGridParams = useMemo(() => {
    if (!contract) {
      return { contractLine: {}, contractEquipment: {}, additionalCost: {} };
    }

    return {
      contractLine: { contract_id: String(contract.id) },
      contractEquipment: { contract_header: contractId, warehouse_id: contractId },
      additionalCost: { contract_header: contractId }
    };
  }, [contract, contractId]);

  const tabConfig: TabItem[] = contract
    ? [
        {
          label: t(`global.base-info`),
          component: (
            <>
              <ContractInsights contract={contract} isVisible={!isSupplierRole} />
              <ContractHeaderForm
                disableEdit={isSupplierRole}
                contract={contract}
                onSubmit={handleFormSubmit}
                suppliers={suppliers}
                laboratories={laboratories}
              />
            </>
          )
        },
        {
          label: t(`pages.contract.new.line-section`),
          visible: !isSupplierRole,
          component: (
            <>
              <ContractLineForm
                onSubmit={onLineSubmit}
                products={products}
                warehouses={warehouses}
              />
              <EnhancedCard
                title={t(`pages.contract.new.line-section`)}
                buttons={
                  isTenantAdmin && (
                    <Button variant="outlined" color="warning" onClick={openResetDialog}>
                      {t('pages.contract.resetResidualUnits.button')}
                    </Button>
                  )
                }>
                {statusOptions && (
                  <EnhancedDataGrid
                    columns={contractLineColumns}
                    service={contractsApi.services.contractLineService}
                    outerParams={memoizedGridParams.contractLine}
                    refresh={contractLinesRefresh}
                    disableDelete={isSupplierRole}
                  />
                )}
              </EnhancedCard>
            </>
          )
        },
        {
          label: t(`pages.contract.new.equipments-section`),
          component: (
            <>
              {!isSupplierRole && (
                <ContractEquipmentForm
                  onSubmit={onSubmitContractEquipment}
                  equipments={equipments}
                />
              )}
              <div className="mt-6">
                <EnhancedDataGrid
                  columns={equipmentColumns}
                  service={contractsApi.services.contractEquipmentService}
                  refresh={datagridRefresh}
                  outerParams={memoizedGridParams.contractEquipment}
                  disableDelete={isSupplierRole}
                />
              </div>
            </>
          )
        },
        {
          label: t(`pages.contract.new.additional-costs`),
          component: (
            <>
              {!isSupplierRole && (
                <AdditionalEquipmentCostForm
                  onSubmit={onAdditionalCostSubmit}
                  equipments={equipments}
                />
              )}
              <div className="mt-6">
                <EnhancedDataGrid
                  columns={additionalCostColumns}
                  service={contractsApi.services.additionalEquipmentCostService}
                  refresh={additionalCostsRefresh}
                  outerParams={memoizedGridParams.additionalCost}
                  disableDelete={isSupplierRole}
                />
              </div>
            </>
          )
        }
      ]
    : [];

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`, { id: contract?.id })}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={contract?.code}>
      <EnhancedTabs
        tabs={tabConfig}
        variant="scrollable"
        documentConfig={{
          serviceName: 'contract',
          entityId: contract?.id,
          dictParams: { contract: contractId },
          customFolderName: `menu.contracts/${contract?.code}`,
          folderDescription: `ID: ${contract?.id}; `,
          disableEdit: isSupplierRole
        }}
        historyConfig={{ service: contractService, entityId: contract?.id }}
        attachmentsConfig={{
          fields: DOWNLOADABLE_FIELDS,
          entity: contract,
          service: contractService,
          visible: !isSupplierRole,
          translationPrefix: 'entities.contract'
        }}
      />

      <UpdateContractLineDialog
        contractLine={editContractLineRow}
        onClose={setCloseDialog}
        open={openEditDialog}
        warehouses={warehouses}
        contractCode={contract?.code}
        handleRefreshDataGrid={handleRefreshLines}
      />

      <EnhancedDialog
        open={openEditEquipmentDialog}
        onClose={setCloseEquipmentDialog}
        title={t('pages.contract-equipment.edit.title', {
          code: editEquipmentRow?.equipment ?? ''
        })}
        submitLabel={t('actions.save', 'Salva')}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="md"
        submitButtonProps={{
          type: 'submit',
          form: 'updateContracEquipment'
        }}>
        {editEquipmentRow && (
          <UpdateContractEquipmentDialog
            contractEquipment={editEquipmentRow}
            onSubmit={onEditContractEquipment}
          />
        )}
      </EnhancedDialog>

      {/* Threshold Dialog */}
      <EnhancedDialog
        open={thresholdDialogOpen}
        onClose={handleClose}
        title={t(`${translationPrefix}.add-threshold`)}
        submitLabel={t('actions.save', 'Salva')}
        cancelLabel={t('actions.reset', 'Annulla')}
        maxWidth="lg"
        submitButtonProps={{
          type: 'submit',
          form: 'addProductThreshold'
        }}>
        <ProductWarehouseForm
          products={products}
          categories={categories}
          statusOptions={statusOptions}
          contracts={contracts}
          initialContract={contract}
          onSubmit={addThreshold}
          labelOptions={labelOptions}
          initialValues={{
            product: selectedProductId
          }}
          warehouseId={selectedWarehouseId}
        />
      </EnhancedDialog>

      <ResetResidualUnitsPreviewDialog
        open={openResetPreviewDialog}
        rows={resetPreviewRows}
        selectedLineIds={selectedResetLineIds}
        isLoadingPreview={isLoadingResetPreview}
        isApplying={isApplyingReset}
        allSelected={allResetLinesSelected}
        onClose={closeResetDialog}
        onToggleLine={toggleResetLineSelection}
        onSelectAll={selectAllResetLines}
        onDeselectAll={deselectAllResetLines}
        onApply={applyResetResidualUnits}
      />
    </TenantPage>
  );
};

export default EditContract;
